/**
 * MoveAccessLogisticsPage — Origin & Destination logistics (strides, waypoints, etc.)
 *
 * Only relevant for Commercial moves. Mirrors the logistics cards from the
 * old Dates & Logistics page but lives as its own sub-page in the unified move view.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Armchair,
  ArrowDown,
  ArrowLeft,
  ArrowUpDown,
  ChevronDown,
  ChevronsUp,
  Footprints,
  Layers,
  LoaderCircle,
  ToggleLeft,
  ToggleRight,
  Truck,
} from 'lucide-react';
import { useSharedQuote } from '../../context/QuoteContext';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { ReadOnlyBanner } from '../../components/ui/ReadOnlyBanner';
import { LogisticsData, WaypointType } from '../../types/admin';

// ── Constants ────────────────────────────────────────────────────────────────

const WAYPOINTS: WaypointType[] = ['Truck', 'Stairs', 'Elevator'];

// ── Logistics form state ─────────────────────────────────────────────────────

interface LogisticsFormState {
  strides1: string;
  waypoint: WaypointType;
  stories: string;
  strides2: string;
  loadingDock: boolean;
}

const DEFAULT_LOGISTICS: LogisticsFormState = {
  strides1: '',
  waypoint: 'Truck',
  stories: '',
  strides2: '',
  loadingDock: false,
};

function fromLogisticsData(data?: LogisticsData): LogisticsFormState {
  return {
    strides1: data?.strides1 ? String(data.strides1) : '',
    waypoint: data?.waypoint ?? 'Truck',
    stories: data?.stories ? String(data.stories) : '',
    strides2: data?.strides2 ? String(data.strides2) : '',
    loadingDock: data?.loadingDock ?? false,
  };
}

function toLogisticsData(
  state: LogisticsFormState,
  includeLoadingDock: boolean,
): LogisticsData {
  const s1 = parseInt(state.strides1, 10);
  const s2 = parseInt(state.strides2, 10);
  const sto = parseInt(state.stories, 10);
  const hasS1 = !isNaN(s1) && s1 > 0;
  const hasS2 = state.waypoint !== 'Truck' && !isNaN(s2) && s2 > 0;
  const hasSto = state.waypoint === 'Stairs' && !isNaN(sto) && sto > 0;
  return {
    strides1: hasS1 ? s1 : undefined,
    distanceFeet1: hasS1 ? s1 * 3 : undefined,
    waypoint: state.waypoint,
    stories: hasSto ? sto : undefined,
    strides2: hasS2 ? s2 : undefined,
    distanceFeet2: hasS2 ? s2 * 3 : undefined,
    loadingDock: includeLoadingDock ? state.loadingDock : undefined,
  };
}

// ── Flow chart sub-components ────────────────────────────────────────────────

function FlowNode({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 shrink-0 rounded-xl bg-mist flex items-center justify-center">
        {icon}
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

function FlowConnector() {
  return (
    <div className="flex gap-3 py-0.5">
      <div className="w-9 shrink-0 flex justify-center">
        <ArrowDown size={13} className="text-stone" />
      </div>
    </div>
  );
}

function WaypointIcon({ type }: { type: WaypointType }) {
  if (type === 'Stairs') return <ChevronsUp size={16} className="text-slate" />;
  if (type === 'Elevator') return <ArrowUpDown size={16} className="text-slate" />;
  return <Truck size={16} className="text-slate" />;
}

function StridesInput({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const n = parseInt(value, 10);
  const feet = !isNaN(n) && n > 0 ? n * 3 : null;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <label htmlFor={id} className="text-small font-medium text-midnight">Strides</label>
      <input
        id={id}
        type="number"
        min="1"
        step="1"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-16 h-9 text-center rounded-lg border border-stone bg-white
                   text-small font-semibold text-midnight
                   focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                   transition-colors"
        placeholder="0"
      />
      {feet !== null && <span className="text-[12px] text-slate">({feet} ft)</span>}
    </div>
  );
}

function WaypointSelector({
  value,
  onChange,
  truckLabel = 'Truck',
}: {
  value: WaypointType;
  onChange: (w: WaypointType) => void;
  truckLabel?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[11px] font-bold uppercase tracking-widest text-slate">Via</p>
      <div className="flex gap-1.5">
        {WAYPOINTS.map(w => (
          <button
            key={w}
            type="button"
            onClick={() => onChange(w)}
            className={`flex-1 h-9 rounded-lg border text-[12px] font-semibold
              transition-all duration-150
              ${value === w
                ? 'border-plum bg-plum/5 text-plum'
                : 'border-stone bg-white text-midnight hover:border-plum/40'
              }`}
          >
            {w === 'Truck' ? truckLabel : w}
          </button>
        ))}
      </div>
    </div>
  );
}

function LoadingDockToggle({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-2">
      <button
        type="button"
        onClick={() => onChange(!value)}
        className="shrink-0 mt-0.5"
        aria-label="Toggle truck-height loading dock"
      >
        {value
          ? <ToggleRight size={26} className="text-plum" />
          : <ToggleLeft size={26} className="text-slate" />
        }
      </button>
      <div>
        <p className="text-small font-medium text-midnight">Truck-height loading dock</p>
        <p className="text-[12px] text-slate leading-snug mt-0.5">Available at this location</p>
      </div>
    </div>
  );
}

// ── LogisticsFlowChart ───────────────────────────────────────────────────────

function LogisticsFlowChart({
  direction,
  isInternalMove,
  value,
  onChange,
  idPrefix,
}: {
  direction: 'origin' | 'destination';
  isInternalMove: boolean;
  value: LogisticsFormState;
  onChange: (patch: Partial<LogisticsFormState>) => void;
  idPrefix: string;
}) {
  const noIntermediateStop = value.waypoint === 'Truck';

  const waypointNode = (
    <FlowNode icon={<WaypointIcon type={value.waypoint} />}>
      <div className="flex flex-col gap-2">
        <WaypointSelector
          value={value.waypoint}
          truckLabel={direction === 'destination' ? 'None' : (isInternalMove ? 'Same floor' : 'Truck')}
          onChange={w => onChange({
            waypoint: w,
            strides2: w === 'Stairs' ? value.strides2 : '',
            stories: w === 'Stairs' ? (value.stories || '1') : '',
          })}
        />
        {value.waypoint === 'Stairs' && (
          <div className="flex items-center gap-2">
            <Layers size={14} className="text-slate shrink-0" />
            <label
              htmlFor={`${idPrefix}-stories`}
              className="text-small font-medium text-midnight whitespace-nowrap"
            >
              Stories
            </label>
            <input
              id={`${idPrefix}-stories`}
              type="number"
              min="1"
              step="1"
              value={value.stories}
              onChange={e => onChange({ stories: e.target.value })}
              className="w-14 h-9 text-center rounded-lg border border-stone bg-white
                         text-small font-semibold text-midnight
                         focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                         transition-colors"
              placeholder="0"
            />
          </div>
        )}
      </div>
    </FlowNode>
  );

  const strides2Row = (
    <>
      <FlowConnector />
      <FlowNode icon={<Footprints size={16} className="text-slate" />}>
        <StridesInput
          id={`${idPrefix}-s2`}
          value={value.strides2}
          onChange={v => onChange({ strides2: v })}
        />
      </FlowNode>
      <FlowConnector />
    </>
  );

  if (direction === 'origin') {
    return (
      <div className="flex flex-col gap-0">
        <FlowNode icon={<Armchair size={16} className="text-slate" />}>
          <p className="text-small font-medium text-midnight">Mid-point on origin floor</p>
        </FlowNode>
        <FlowConnector />
        <FlowNode icon={<Footprints size={16} className="text-slate" />}>
          <StridesInput
            id={`${idPrefix}-s1`}
            value={value.strides1}
            onChange={v => onChange({ strides1: v })}
          />
        </FlowNode>
        <FlowConnector />
        {waypointNode}
        {!noIntermediateStop ? strides2Row : <FlowConnector />}
        <FlowNode
          icon={
            isInternalMove
              ? <Armchair size={16} className="text-slate" />
              : <Truck size={16} className="text-slate" />
          }
        >
          <p className="text-small font-medium text-midnight">
            {isInternalMove ? 'Same floor' : 'Truck'}
          </p>
        </FlowNode>
        {!isInternalMove && (
          <div className="mt-4 pl-12">
            <LoadingDockToggle
              value={value.loadingDock}
              onChange={v => onChange({ loadingDock: v })}
            />
          </div>
        )}
      </div>
    );
  }

  // destination — inverted: truck → desk
  return (
    <div className="flex flex-col gap-0">
      <div className="mb-4 pl-12">
        <LoadingDockToggle
          value={value.loadingDock}
          onChange={v => onChange({ loadingDock: v })}
        />
      </div>
      <FlowNode icon={<Truck size={16} className="text-slate" />}>
        <p className="text-small font-medium text-midnight">Truck</p>
      </FlowNode>
      <FlowConnector />
      <FlowNode icon={<Footprints size={16} className="text-slate" />}>
        <StridesInput
          id={`${idPrefix}-s1`}
          value={value.strides1}
          onChange={v => onChange({ strides1: v })}
        />
      </FlowNode>
      <FlowConnector />
      {waypointNode}
      {!noIntermediateStop ? strides2Row : <FlowConnector />}
      <FlowNode icon={<Armchair size={16} className="text-slate" />}>
        <p className="text-small font-medium text-midnight">Mid-point on destination floor</p>
      </FlowNode>
    </div>
  );
}

// ── Collapsible section ──────────────────────────────────────────────────────

function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-visible">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-4 py-4
                   hover:bg-mist active:bg-stone transition-colors duration-150
                   pointer-events-auto ${open ? 'rounded-t-xl' : 'rounded-xl'}`}
      >
        <span className="font-semibold text-small text-midnight">{title}</span>
        <ChevronDown
          size={18}
          className={`text-slate transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-4 pb-5 pt-1 flex flex-col gap-4 border-t border-stone overflow-x-hidden">
          {children}
        </div>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function MoveAccessLogisticsPage() {
  const navigate = useNavigate();
  const { quote, loading, updateQuote } = useSharedQuote();

  const [initialized, setInitialized] = useState(false);
  const justInitialized = useRef(false);

  const [origLogistics, setOrigLogistics] = useState<LogisticsFormState>(DEFAULT_LOGISTICS);
  const [destLogistics, setDestLogistics] = useState<LogisticsFormState>(DEFAULT_LOGISTICS);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const internalMove = quote?.addresses?.internalMove ?? false;

  // ── Initialize from quote ────────────────────────────────────────────────
  useEffect(() => {
    if (quote && !loading && !initialized) {
      justInitialized.current = true;
      setOrigLogistics(fromLogisticsData(quote.originLogistics));
      setDestLogistics(fromLogisticsData(quote.destinationLogistics));
      setInitialized(true);
    }
  }, [quote, loading, initialized]);

  // ── Auto-save ────────────────────────────────────────────────────────────
  const doAutoSave = useCallback(() => {
    if (quote?.quoteStatus !== 'draft') return;
    setSaving(true);
    setSaveError('');
    updateQuote({
      originLogistics: toLogisticsData(origLogistics, !internalMove),
      destinationLogistics: internalMove ? undefined : toLogisticsData(destLogistics, true),
    })
      .then(() => setSaving(false))
      .catch(err => {
        setSaveError(err instanceof Error ? err.message : 'Auto-save failed');
        setSaving(false);
      });
  }, [origLogistics, destLogistics, internalMove, updateQuote, quote?.quoteStatus]);

  useEffect(() => {
    if (!initialized) return;
    if (justInitialized.current) { justInitialized.current = false; return; }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(doAutoSave, 800);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [doAutoSave, initialized]);

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading || !quote) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const isReadOnly = quote.quoteStatus !== 'draft';

  return (
    <div className="px-4 pt-4 pb-8 max-w-2xl">
      {/* Mobile back */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="md:hidden flex items-center gap-1 text-small text-slate hover:text-midnight
                   mb-3 transition-colors duration-150 -ml-1"
      >
        <ArrowLeft size={16} />
        Back
      </button>

      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display text-h1 text-midnight">Access Logistics</h1>
        {saving && <LoaderCircle size={16} className="text-slate animate-spin shrink-0" />}
      </div>

      {isReadOnly && <ReadOnlyBanner status={quote.quoteStatus} />}

      {saveError && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 rounded-xl bg-rose/10 border border-rose/30">
          <AlertCircle size={15} className="shrink-0 text-rose mt-0.5" />
          <p className="text-small text-rose">{saveError}</p>
        </div>
      )}

      <div className={isReadOnly ? 'pointer-events-none opacity-60' : ''}>
        <div className="flex flex-col gap-3">
          {/* Origin Logistics */}
          <CollapsibleSection title="Origin Logistics" defaultOpen>
            <LogisticsFlowChart
              direction="origin"
              isInternalMove={internalMove}
              value={origLogistics}
              onChange={patch => setOrigLogistics(prev => ({ ...prev, ...patch }))}
              idPrefix="al-orig"
            />
          </CollapsibleSection>

          {/* Destination Logistics (hide for internal move) */}
          {!internalMove && (
            <CollapsibleSection title="Destination Logistics" defaultOpen>
              <LogisticsFlowChart
                direction="destination"
                isInternalMove={false}
                value={destLogistics}
                onChange={patch => setDestLogistics(prev => ({ ...prev, ...patch }))}
                idPrefix="al-dest"
              />
            </CollapsibleSection>
          )}
        </div>
      </div>
    </div>
  );
}
